import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FieldsLayout } from "./layout";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Play,
  Square,
  Pause,
  PlayCircle,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  getAllFieldSyncStatus,
  startFieldSync,
  stopFieldSync,
  pauseFieldSync,
  resumeFieldSync,
  flagAllForFieldSync,
  getFieldSyncHistory,
  FieldSyncAllStatusResponse,
  FieldSyncHistoryItem,
} from "@/lib/api";

// ============================================================================
// Constants & Helpers
// ============================================================================

const ENTITY_TYPES = ["Series", "Title", "Contract", "MainSeries", "Product"] as const;

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-GB", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  } catch {
    return "-";
  }
};

const formatDuration = (startedAt?: string, completedAt?: string): string => {
  if (!startedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  if (isNaN(start)) return "-";
  const diffMs = end - start;
  if (diffMs < 1000) return `${diffMs}ms`;
  if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s`;
  const mins = Math.floor(diffMs / 60_000);
  const secs = Math.round((diffMs % 60_000) / 1000);
  return `${mins}m ${secs}s`;
};

type EntityStatus = FieldSyncAllStatusResponse[string];

function getStatusBadge(entity: EntityStatus) {
  if (entity.isRunning) {
    if (entity.currentJob?.status === "Paused" || entity.isPaused) {
      return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">Paused</Badge>;
    }
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Running</Badge>;
  }
  if (entity.pendingCount > 0) {
    return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Pending</Badge>;
  }
  return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Idle</Badge>;
}

function getHistoryStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === "completed" || s === "succeeded") {
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">{status}</Badge>;
  }
  if (s === "failed") {
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">{status}</Badge>;
  }
  if (s === "cancelled" || s === "stopped") {
    return <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">{status}</Badge>;
  }
  if (s === "running") {
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">{status}</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
}

// ============================================================================
// Entity Card Component
// ============================================================================

interface EntityCardProps {
  entityType: string;
  data: EntityStatus;
  onStart: (entityType: string, maxRecords?: number) => void;
  onStop: (entityType: string) => void;
  onPause: (entityType: string) => void;
  onResume: (entityType: string) => void;
  onFlagAll: (entityType: string) => void;
  isMutating: boolean;
}

function EntityCard({
  entityType,
  data,
  onStart,
  onStop,
  onPause,
  onResume,
  onFlagAll,
  isMutating,
}: EntityCardProps) {
  const [maxRecordsInput, setMaxRecordsInput] = useState("");
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);
  const [flagAllDialogOpen, setFlagAllDialogOpen] = useState(false);

  const isRunning = data.isRunning;
  const isPaused = data.currentJob?.status === "Paused" || data.isPaused;
  const currentJob = data.currentJob;

  const handleStart = () => {
    const maxRecords = maxRecordsInput ? parseInt(maxRecordsInput, 10) : undefined;
    onStart(entityType, maxRecords);
    setStartPopoverOpen(false);
    setMaxRecordsInput("");
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{entityType}</CardTitle>
          {getStatusBadge(data)}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Metrics */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Pending</span>
            <p className="font-semibold text-lg">{data.pendingCount.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Mappings</span>
            <p className="font-semibold text-lg">{data.mappingCount}</p>
          </div>
        </div>

        {/* Progress when running */}
        {isRunning && currentJob && (
          <div className="text-xs text-muted-foreground space-y-1 border-t pt-2">
            <div className="flex justify-between">
              <span>Progress</span>
              <span>
                {currentJob.processedRecords ?? 0} / {currentJob.totalRecords ?? "?"}
              </span>
            </div>
            {currentJob.totalRecords && currentJob.totalRecords > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, ((currentJob.processedRecords ?? 0) / currentJob.totalRecords) * 100)}%`,
                  }}
                />
              </div>
            )}
            <div className="flex justify-between">
              <span>Elapsed</span>
              <span>{formatDuration(currentJob.startedAt)}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 border-t pt-2">
          {!isRunning && (
            <Popover open={startPopoverOpen} onOpenChange={setStartPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isMutating || !data.hasMappings}
                  title={!data.hasMappings ? "No field mappings configured" : "Start field sync"}
                >
                  <Play className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="start">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Start Field Sync</p>
                  <Input
                    type="number"
                    placeholder="Max records (all)"
                    value={maxRecordsInput}
                    onChange={(e) => setMaxRecordsInput(e.target.value)}
                    min={1}
                  />
                  <Button size="sm" className="w-full" onClick={handleStart} disabled={isMutating}>
                    {isMutating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Start
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {isRunning && !isPaused && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPause(entityType)}
                disabled={isMutating}
                title="Pause"
              >
                <Pause className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStop(entityType)}
                disabled={isMutating}
                title="Stop"
              >
                <Square className="h-4 w-4" />
              </Button>
            </>
          )}

          {isPaused && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResume(entityType)}
              disabled={isMutating}
              title="Resume"
            >
              <PlayCircle className="h-4 w-4" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFlagAllDialogOpen(true)}
            disabled={isMutating || isRunning}
            title="Flag all records for re-sync"
            className="ml-auto"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>

      {/* Flag All confirmation */}
      <AlertDialog open={flagAllDialogOpen} onOpenChange={setFlagAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Flag all {entityType} records for re-sync?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set NeedsFieldSync = 1 on every record in the {entityType} master table.
              The next field sync run will re-process all records, which may take a while for large entities.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onFlagAll(entityType)}
            >
              Flag All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ============================================================================
// Main Page
// ============================================================================

const FieldSyncPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track which entity is currently being mutated
  const [mutatingEntity, setMutatingEntity] = useState<string | null>(null);

  // ---- Queries ----

  const statusQuery = useQuery({
    queryKey: ["field-sync-status"],
    queryFn: getAllFieldSyncStatus,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 30_000;
      const anyRunning = Object.values(data).some((e) => e.isRunning);
      return anyRunning ? 5_000 : 30_000;
    },
  });

  const historyQuery = useQuery({
    queryKey: ["field-sync-history"],
    queryFn: () => getFieldSyncHistory(20),
    refetchInterval: 30_000,
  });

  const statusData = statusQuery.data;
  const historyData = historyQuery.data;

  // ---- Mutations ----

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["field-sync-status"] });
    queryClient.invalidateQueries({ queryKey: ["field-sync-history"] });
  };

  const startMutation = useMutation({
    mutationFn: ({ entityType, maxRecords }: { entityType: string; maxRecords?: number }) =>
      startFieldSync(entityType, maxRecords),
    onSuccess: (data, variables) => {
      toast({
        title: `Field sync started for ${variables.entityType}`,
        description: data.totalRecords != null
          ? `${data.totalRecords} records queued in ${data.totalChunks} chunks.`
          : undefined,
      });
      invalidateAll();
    },
    onError: (error: Error, variables) => {
      toast({
        title: `Failed to start ${variables.entityType}`,
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => setMutatingEntity(null),
  });

  const stopMutation = useMutation({
    mutationFn: stopFieldSync,
    onSuccess: (_, entityType) => {
      toast({ title: `Field sync stopped for ${entityType}` });
      invalidateAll();
    },
    onError: (error: Error, entityType) => {
      toast({ title: `Failed to stop ${entityType}`, description: error.message, variant: "destructive" });
    },
    onSettled: () => setMutatingEntity(null),
  });

  const pauseMutation = useMutation({
    mutationFn: pauseFieldSync,
    onSuccess: (_, entityType) => {
      toast({ title: `Field sync paused for ${entityType}` });
      invalidateAll();
    },
    onError: (error: Error, entityType) => {
      toast({ title: `Failed to pause ${entityType}`, description: error.message, variant: "destructive" });
    },
    onSettled: () => setMutatingEntity(null),
  });

  const resumeMutation = useMutation({
    mutationFn: resumeFieldSync,
    onSuccess: (_, entityType) => {
      toast({ title: `Field sync resumed for ${entityType}` });
      invalidateAll();
    },
    onError: (error: Error, entityType) => {
      toast({ title: `Failed to resume ${entityType}`, description: error.message, variant: "destructive" });
    },
    onSettled: () => setMutatingEntity(null),
  });

  const flagAllMutation = useMutation({
    mutationFn: flagAllForFieldSync,
    onSuccess: (data, entityType) => {
      toast({
        title: `Flagged ${data.flaggedCount.toLocaleString()} records for ${entityType}`,
        description: "Records will be processed on the next field sync run.",
      });
      invalidateAll();
    },
    onError: (error: Error, entityType) => {
      toast({ title: `Failed to flag records for ${entityType}`, description: error.message, variant: "destructive" });
    },
    onSettled: () => setMutatingEntity(null),
  });

  // ---- Action handlers ----

  const handleStart = (entityType: string, maxRecords?: number) => {
    setMutatingEntity(entityType);
    startMutation.mutate({ entityType, maxRecords });
  };

  const handleStop = (entityType: string) => {
    setMutatingEntity(entityType);
    stopMutation.mutate(entityType);
  };

  const handlePause = (entityType: string) => {
    setMutatingEntity(entityType);
    pauseMutation.mutate(entityType);
  };

  const handleResume = (entityType: string) => {
    setMutatingEntity(entityType);
    resumeMutation.mutate(entityType);
  };

  const handleFlagAll = (entityType: string) => {
    setMutatingEntity(entityType);
    flagAllMutation.mutate(entityType);
  };

  // ---- Summary stats ----

  const summary = useMemo(() => {
    if (!statusData) return null;
    const entries = Object.values(statusData);
    return {
      totalPending: entries.reduce((sum, e) => sum + (e.pendingCount || 0), 0),
      totalMappings: entries.reduce((sum, e) => sum + (e.mappingCount || 0), 0),
      runningCount: entries.filter((e) => e.isRunning).length,
    };
  }, [statusData]);

  // ---- Render ----

  const handleRefresh = () => {
    statusQuery.refetch();
    historyQuery.refetch();
  };

  return (
    <FieldsLayout onRefresh={handleRefresh} isRefreshing={statusQuery.isFetching}>
      <PageWrapper padding="small">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold dark:text-zinc-100">Field Sync</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Copies values from KUP fields to HS fields in Dataverse. Triggered automatically after SQL Sync, or start manually below.
            </p>
          </div>

          {/* Summary bar */}
          {summary && (
            <div className="flex gap-4 text-sm">
              <span>
                <span className="text-muted-foreground">Total pending:</span>{" "}
                <span className="font-medium">{summary.totalPending.toLocaleString()}</span>
              </span>
              <span>
                <span className="text-muted-foreground">Mappings:</span>{" "}
                <span className="font-medium">{summary.totalMappings}</span>
              </span>
              {summary.runningCount > 0 && (
                <span>
                  <span className="text-muted-foreground">Running:</span>{" "}
                  <span className="font-medium text-blue-700">{summary.runningCount}</span>
                </span>
              )}
            </div>
          )}

          {/* Entity Status Cards */}
          {statusQuery.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {ENTITY_TYPES.map((et) => (
                <Card key={et}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-5 w-24" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : statusQuery.isError ? (
            <Card>
              <CardContent className="py-6">
                <p className="text-sm text-red-600">
                  Failed to load field sync status: {(statusQuery.error as Error)?.message || "Unknown error"}
                </p>
              </CardContent>
            </Card>
          ) : statusData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {ENTITY_TYPES.map((et) => {
                const data = statusData[et];
                if (!data) return null;
                return (
                  <EntityCard
                    key={et}
                    entityType={et}
                    data={data}
                    onStart={handleStart}
                    onStop={handleStop}
                    onPause={handlePause}
                    onResume={handleResume}
                    onFlagAll={handleFlagAll}
                    isMutating={mutatingEntity === et}
                  />
                );
              })}
            </div>
          ) : null}

          {/* Job History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {historyQuery.isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : historyQuery.isError ? (
                <p className="text-sm text-red-600">
                  Failed to load history: {(historyQuery.error as Error)?.message || "Unknown error"}
                </p>
              ) : historyData && historyData.history.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entity</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Records</TableHead>
                        <TableHead className="text-right">OK</TableHead>
                        <TableHead className="text-right">Failed</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.history.map((job: FieldSyncHistoryItem) => (
                        <TableRow key={job.jobId}>
                          <TableCell className="font-medium">{job.entityType}</TableCell>
                          <TableCell>{getHistoryStatusBadge(job.status)}</TableCell>
                          <TableCell className="text-right">{job.totalRecords?.toLocaleString() ?? "-"}</TableCell>
                          <TableCell className="text-right">{job.successfulRecords?.toLocaleString() ?? "-"}</TableCell>
                          <TableCell className="text-right">
                            {job.failedRecords ? (
                              <span className="text-red-600">{job.failedRecords.toLocaleString()}</span>
                            ) : (
                              "0"
                            )}
                          </TableCell>
                          <TableCell>{formatDate(job.startedAt)}</TableCell>
                          <TableCell>{formatDuration(job.startedAt, job.completedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No job history yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    </FieldsLayout>
  );
};

export default FieldSyncPage;
